import type { MenuDefinition } from '@/shared/layouts/menu/types';
import type { MeMenu, MePermissions, RbacMenuGroup, RbacMenuTab } from '../types/permissions';
import { projectKeilogMenu } from './conceptMenu';

const EMPTY_MENU: MenuDefinition = { tabs: [] };
let currentMenu: MenuDefinition = EMPTY_MENU;

/** The one untitled tab a schema-2 payload (a flat list of groups) is drawn in. */
const UNIFIED_TAB_ID = 'unified';

const cloneMenu = (menu: MenuDefinition): MenuDefinition => ({
  tabs: menu.tabs.map(tab => ({
    ...tab,
    groups: tab.groups.map(group => ({
      ...group,
      ...(group.items ? { items: group.items.map(entry => ({ ...entry })) } : {}),
    })),
  })),
});

/** Pure adapter used by PermissionContext for the active role's server-filtered tree. */
export const menuDefinitionFromTabs = (tabs: readonly RbacMenuTab[]): MenuDefinition =>
  projectKeilogMenu(tabs);

/** A schema-2 flat list of groups, drawn as one untitled tab. */
export const menuDefinitionFromGroups = (groups: RbacMenuGroup[]): MenuDefinition =>
  menuDefinitionFromTabs([{ id: UNIFIED_TAB_ID, label: '', groups }]);

/** The sidebar of one `/me/permissions` snapshot (schema 3 tabs, else the schema-2 groups). */
export const menuDefinitionFromPermissions = (
  payload: Pick<MePermissions, 'menu' | 'menuTabs'>
): MenuDefinition =>
  payload.menuTabs ? menuDefinitionFromTabs(payload.menuTabs) : menuDefinitionFromGroups(payload.menu);

export function readStoredMenu(): MenuDefinition {
  return cloneMenu(currentMenu);
}

export function writeStoredMenu(menu: MeMenu | RbacMenuGroup[]): void {
  currentMenu = Array.isArray(menu) ? menuDefinitionFromGroups(menu) : menuDefinitionFromTabs(menu.tabs);
}

/** Loading, logout, malformed payloads and request failures all hide the menu. */
export function clearStoredMenu(): void {
  currentMenu = EMPTY_MENU;
}

export function resetMenuSnapshot(): void {
  currentMenu = EMPTY_MENU;
}
